// Flux temps réel partagé (R2) — UNE seule connexion SSE pour toute l'app.
//
// La cloche (useNotifications) et les listes vivantes (ListeCommande…) s'abonnent
// tous à cette même connexion via subscribeRealtime(handler). On évite ainsi
// d'ouvrir plusieurs EventSource par client. Le flux pousse des lots
// { last_id, notifications:[{id_event, famille, action, id_commande, ...}] } —
// chaque handler filtre ce qui l'intéresse.
//
// Auth : ticket court récupéré via GET /api/stream/ticket (l'EventSource ne peut
// pas poser d'en-tête). Reconnexion gérée à la main avec un ticket frais ; reprise
// via after_id (le serveur rejoue depuis le dernier événement vu).
//
// R3 : le flux est actif sur le web ET sur l'APK. L'APK figée actuelle ne le prend
// qu'à sa prochaine compilation ; d'ici là elle continue de sonder. Le sondage 45 s
// (useNotifications) reste le filet de sécurité sur toutes les plateformes.

import axios from 'axios';
import { store } from './redux/store';
import config from './config';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

const handlers = new Set();
let es = null;
let retry = null;
let cursor = 0;      // dernier last_id vu (reprise à la reconnexion)
let connecting = false;
let connectedUserId = null; // utilisateur dont le ticket a ouvert le flux courant
let unsubStore = null;      // surveillance du store (changement d'utilisateur)

const currentUserId = () => {
  try { return store.getState()?.user?.currentUser?.id || null; } catch (e) { return null; }
};

const dispatch = (data) => {
  handlers.forEach((h) => { try { h(data); } catch (e) { /* un handler ne casse pas les autres */ } });
};

const scheduleReconnect = (ms) => {
  clearTimeout(retry);
  retry = setTimeout(connect, ms);
};

async function connect() {
  if (connecting || es || !handlers.size) return;
  // R3 : le flux est AUSSI actif sur l'APK (validé avec le nouvel APK ; le build
  // figé actuel n'est pas concerné). Repli : le sondage 45 s de useNotifications
  // reste le filet si le flux natif échoue.
  const uid = currentUserId();
  if (!uid) { scheduleReconnect(4000); return; } // pas encore connecté
  connecting = true;
  try {
    const { data } = await axios.get(`${DOMAIN}/api/stream/ticket`);
    if (!handlers.size) { connecting = false; return; }
    if (!data || !data.ticket) { connecting = false; scheduleReconnect(8000); return; }
    const url = `${DOMAIN}/api/stream?ticket=${encodeURIComponent(data.ticket)}&after_id=${cursor || 0}`;
    es = new EventSource(url);
    connectedUserId = uid; // le flux courant appartient à cet utilisateur
    es.addEventListener('notif', (ev) => {
      try {
        const d = JSON.parse(ev.data);
        if (d && d.last_id) cursor = Math.max(cursor, Number(d.last_id) || 0);
        dispatch(d);
      } catch (e) { /* payload illisible : ignore */ }
    });
    es.onerror = () => {
      try { es.close(); } catch (e) { /* ignore */ }
      es = null;
      if (handlers.size) scheduleReconnect(5000);
    };
  } catch (e) {
    scheduleReconnect(8000);
  } finally {
    connecting = false;
  }
}

/**
 * Abonne un handler au flux temps réel. Ouvre la connexion au premier abonné,
 * la ferme quand il n'y a plus d'abonné. Renvoie une fonction de désabonnement.
 * `initialCursor` (optionnel) : avance le curseur de reprise (ex. le curseur
 * persisté de la cloche) pour ne pas rejouer d'anciens événements.
 */
// Ferme proprement le flux courant (sans toucher aux abonnés).
function closeStream() {
  clearTimeout(retry);
  if (es) { try { es.close(); } catch (e) { /* ignore */ } es = null; }
  connectedUserId = null;
}

// Surveille le store : si l'utilisateur change (login sous un autre compte SANS
// rechargement complet), on ferme le flux de l'ancien utilisateur et on rouvre
// avec le ticket du nouveau — le flux ne reste JAMAIS lié à un contexte périmé
// (défaut latent relevé en revue R2, on ne dépend pas d'un reload externe).
function watchUser() {
  if (unsubStore) return;
  unsubStore = store.subscribe(() => {
    const uid = currentUserId();
    if (es && connectedUserId != null && uid !== connectedUserId) {
      closeStream();
      cursor = 0; // nouveau contexte utilisateur : on ne rejoue pas l'ancien curseur
      if (handlers.size) scheduleReconnect(50);
    }
  });
}

export function subscribeRealtime(handler, { initialCursor } = {}) {
  if (typeof initialCursor === 'number' && initialCursor > cursor) cursor = initialCursor;
  handlers.add(handler);
  watchUser();
  if (!es && !connecting) connect();
  return () => {
    handlers.delete(handler);
    if (handlers.size === 0) {
      closeStream();
      if (unsubStore) { unsubStore(); unsubStore = null; }
    }
  };
}
